import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Plus, ArrowRight, Eye, Loader2, Trash2, Copy, Check, QrCode } from 'lucide-react';
import { isAccountLoggedIn, getAccessToken } from '../accountAuth';
import { getMyAccount, deleteMyCode, AccountCode } from '../api/accountApi';
import { Button, Card, Alert } from './ui';
import { timeAgo } from '../utils/timeAgo';
import { buildQrUrl } from '../config';

const MyCodes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<AccountCode[]>([]);
  const [email, setEmail] = useState('');
  const [confirmDeleteHandle, setConfirmDeleteHandle] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [copiedHandle, setCopiedHandle] = useState<string | null>(null);

  const copyLink = async (handle: string) => {
    await navigator.clipboard.writeText(buildQrUrl(handle));
    setCopiedHandle(handle);
    setTimeout(() => setCopiedHandle(null), 2000);
  };

  useEffect(() => {
    if (!isAccountLoggedIn()) { navigate('/account/login'); return; }
    loadAccount();
  }, []);

  const loadAccount = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMyAccount();
      setCodes(data.codes);
      setEmail(data.email);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (handle: string) => {
    setDeleting(true);
    try {
      await deleteMyCode(handle);
      setCodes(prev => prev.filter(c => c.handle !== handle));
      setConfirmDeleteHandle(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const [tokenChecked, setTokenChecked] = useState(false);
  useEffect(() => {
    getAccessToken().then((token) => {
      if (!token) navigate('/account/login');
      setTokenChecked(true);
    });
  }, []);

  if (!tokenChecked) return null;

  const maxScans = codes.length > 0 ? Math.max(...codes.map(c => c.viewCount), 1) : 1;

  return (
    <>
      <Helmet><title>My Codes — YOWO</title></Helmet>
      <div className="relative z-[1] min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">

          {/* Header */}
          <div className="flex items-center justify-between mb-10">
            <div>
              <h1
                className="text-3xl font-black text-slate-900 dark:text-slate-100"
                style={{ fontFamily: 'Syne, sans-serif', letterSpacing: '-0.03em' }}
              >
                My Codes
              </h1>
              <p className="text-slate-400 dark:text-slate-500 mt-0.5 text-sm">{email}</p>
            </div>
            <Button variant="primary" onClick={() => navigate('/setup')}>
              <Plus size={15} className="mr-1" />
              New Code
            </Button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={32} className="animate-spin text-brand" />
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <Alert variant="danger" className="mb-6">
              {error}
              <button type="button" onClick={loadAccount} className="ml-2 underline">Retry</button>
            </Alert>
          )}

          {/* Codes */}
          {!loading && !error && (
            <>
              {codes.length === 0 ? (
                /* ── Empty state ── */
                <Card className="brand-panel text-center py-20">
                  <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-3xl mb-6"
                    style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.08) 0%, rgba(14,165,183,0.12) 100%)' }}>
                    <QrCode size={32} className="text-brand dark:text-blue-400 opacity-70" />
                    <div className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full flex items-center justify-center shadow-md"
                      style={{ background: 'linear-gradient(125deg, #2563eb, #0891b2)' }}>
                      <Plus size={12} className="text-white" />
                    </div>
                  </div>
                  <h2
                    className="text-2xl font-black text-slate-900 dark:text-slate-100 mb-2"
                    style={{ fontFamily: 'Syne, sans-serif', letterSpacing: '-0.02em' }}
                  >
                    Nothing scannable yet.
                  </h2>
                  <p className="text-slate-500 dark:text-slate-400 mb-8 max-w-xs mx-auto text-sm leading-relaxed">
                    The world is waiting for your first mysterious QR code. We checked.
                  </p>
                  <Button variant="primary" size="lg" onClick={() => navigate('/setup')}>
                    Make Something Scannable
                    <ArrowRight size={16} className="ml-1" />
                  </Button>
                </Card>
              ) : (
                <div className="space-y-3">
                  {codes.map((code, idx) => {
                    const scanPct = Math.round((code.viewCount / maxScans) * 100);
                    const isTopCode = idx === 0 && code.viewCount > 0;
                    return (
                      <Card
                        key={code.handle}
                        className={`brand-panel hover:shadow-lift hover:-translate-y-0.5 transition-all duration-200 !p-0 overflow-hidden ${isTopCode ? 'ring-1 ring-brand/20 dark:ring-blue-500/20' : ''}`}
                      >
                        {/* Scan bar at top */}
                        {code.viewCount > 0 && (
                          <div className="h-0.5 bg-slate-100 dark:bg-slate-800">
                            <div
                              className="h-full transition-all duration-700 rounded-full"
                              style={{
                                width: `${scanPct}%`,
                                background: 'linear-gradient(90deg, #2563eb, #0ea5b7)',
                              }}
                            />
                          </div>
                        )}

                        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex-1 min-w-0">
                            {/* Handle */}
                            <div className="flex items-center gap-2 mb-1.5 min-w-0">
                              <h2
                                className="flex-1 min-w-0 font-black text-slate-900 dark:text-slate-100 text-sm sm:text-base leading-tight whitespace-nowrap"
                                style={{ fontFamily: 'Syne, sans-serif', letterSpacing: '-0.01em' }}
                                title={`/${code.handle}`}
                              >
                                /{code.handle}
                              </h2>
                              {isTopCode && (
                                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                                  style={{ background: 'linear-gradient(125deg, rgba(37,99,235,0.12), rgba(14,165,183,0.12))', color: '#2563eb' }}>
                                  Top
                                </span>
                              )}
                            </div>

                            {/* Message */}
                            <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-1 mb-2">
                              {code.message || <em className="opacity-40">No message set</em>}
                            </p>

                            {/* Meta row */}
                            <div className="flex items-center gap-3 text-xs text-slate-400 dark:text-slate-500">
                              <span className="flex items-center gap-1">
                                <Eye size={10} />
                                {code.viewCount.toLocaleString()} scan{code.viewCount !== 1 ? 's' : ''}
                              </span>
                              <span>·</span>
                              <span>Updated {timeAgo(code.updatedAt)}</span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex flex-wrap gap-1.5 shrink-0 items-center sm:justify-end">
                            {confirmDeleteHandle === code.handle ? (
                              <>
                                <Button variant="secondary" size="sm" onClick={() => setConfirmDeleteHandle(null)} disabled={deleting}>Cancel</Button>
                                <Button variant="danger" size="sm" onClick={() => handleDelete(code.handle)} disabled={deleting}>
                                  {deleting ? '...' : 'Delete'}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => copyLink(code.handle)} aria-label="Copy link" title="Copy link">
                                  {copiedHandle === code.handle ? <Check size={14} className="text-emerald-500" /> : <Copy size={14} />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteHandle(code.handle)} aria-label="Delete" title="Delete code">
                                  <Trash2 size={14} />
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => navigate(`/dashboard/${code.handle}`)}>
                                  Manage
                                  <ArrowRight size={13} className="ml-1" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MyCodes;
